import React, { useEffect } from "react";
import { applySeoOverride } from "../hooks/useSeo";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, ShieldCheck, UserCheck, FileCheck2, MapPin, ArrowRight, Users, Scale, Baby, Clock, Sparkles, HeartHandshake } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useJobModal } from "../context/JobModalContext";
import { CITIES, CITY_SEO } from "../mock";

const SERVICES = [
  { slug: "nanny", label: "Nanny", icon: Baby },
  { slug: "babysitter", label: "Babysitter", icon: Clock },
  { slug: "housekeeper", label: "Housekeeper", icon: Sparkles },
  { slug: "senior-care", label: "Senior care", icon: HeartHandshake },
];

export default function CityPage() {
  const { slug } = useParams();
  const { openJobModal } = useJobModal();
  const city = CITIES.find((c) => c.slug === slug) || { name: "the US", state: "", slug: "" };
  const seo = CITY_SEO[slug] || {};
  const rate = seo?.stats?.avgRate || "$24/hr";

  useEffect(() => {
    document.title = `Nanny & Childcare in ${city.name}, ${city.state} | SimplySitter`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.setAttribute("content", `Find vetted nannies, babysitters, housekeepers & senior care in ${city.name}, ${city.state}. Post your job free \u2014 1 month free, then $15.99/month.`);
    applySeoOverride();
    return () => { document.title = "SimplySitter"; };
  }, [city.name, city.state]);

  const post = () => openJobModal({ location: city.name === "the US" ? "" : city.name, citySlug: city.slug });

  const faqs = [
    { q: `How much does childcare in ${city.name} cost?`, a: `Rates in ${city.name} typically start around ${rate}. With SimplySitter there's no placement commission — you only pay the transparent $15.99/month membership, and your first month is free.` },
    { q: `Are the caregivers in ${city.name} vetted?`, a: `Yes. Every caregiver in ${city.name} verifies their identity, is personally interviewed and passes background and reference checks before they can apply to your job.` },
    { q: `What care can I find in ${city.name}?`, a: `You can find nannies, babysitters, housekeepers and senior caregivers across ${city.name} and the surrounding area — full-time, part-time, occasional or one-time.` },
    { q: "Can I cancel anytime?", a: "Absolutely. Your membership can be canceled at any time from your account — no notice period and no cancellation fees." },
  ];

  return (
    <div>
      <Header />

      {/* Hero */}
      <section className="hero-gradient">
        <div className="max-w-4xl mx-auto px-5 pt-14 pb-16 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white/70 px-4 py-1.5 text-[14px] text-[#5a5254]">
            <MapPin className="w-4 h-4 text-[#7c2233]" /> Care in {city.name}{city.state ? `, ${city.state}` : ""}
          </div>
          <h1 className="font-display text-[2.5rem] leading-[1.08] sm:text-6xl sm:leading-[1.05] text-[#1f1a1b] mt-6">
            Find childcare, nanny & household help in <span className="text-[#7c2233]">{city.name}</span>
          </h1>
          <p className="mt-5 text-[18px] leading-relaxed text-[#5a5254] max-w-2xl mx-auto">
            Find vetted, interviewed and background-checked caregivers in {city.name}. Post your job for free and receive applications from experienced nannies, babysitters and housekeepers.
          </p>
          <button onClick={post} className="inline-flex items-center gap-2 mt-8 rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-8 py-4 shadow-sm transition-colors">
            Post a job free <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {[[ShieldCheck, "Identity verified"], [UserCheck, "Personally interviewed"], [FileCheck2, "Background checked"]].map(([Icon, t]) => (
              <span key={t} className="flex items-center gap-2 text-[14px] text-[#3a3436]"><Icon className="w-4 h-4 text-[#7c2233]" strokeWidth={1.8} />{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 py-16 grid md:grid-cols-3 gap-5">
          {[[ShieldCheck, `Vetted caregivers in ${city.name}`, `Every caregiver in ${city.name} is identity-verified, interviewed and background-checked — for your family's maximum safety.`],
            [Users, `Relevant applications from ${city.name}`, `You receive applications from caregivers directly in ${city.name} and surroundings — matched to your schedule and needs.`],
            [Scale, "Fair & flexible", `No placement commission. You only pay the transparent membership and find your caregiver in ${city.name} at your own pace.`]].map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl border border-black/[0.07] p-7">
              <div className="w-12 h-12 rounded-xl bg-[#f7e4e6] flex items-center justify-center"><Icon className="w-6 h-6 text-[#7c2233]" strokeWidth={1.7} /></div>
              <h3 className="font-display text-xl text-[#1f1a1b] mt-4">{t}</h3>
              <p className="text-[15px] text-[#5a5254] mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3 steps */}
      <section className="bg-[#fdfaf7]">
        <div className="max-w-4xl mx-auto px-5 py-16">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1f1a1b]">Find your caregiver in {city.name} in 3 steps</h2>
          <div className="mt-8 space-y-4">
            {[[`Post a job for ${city.name}`, `In a few minutes, describe the caregiver you're looking for in ${city.name} — tasks, hours and special wishes.`],
              ["Receive applications", `Vetted caregivers from ${city.name} apply to your listing as soon as it's live.`],
              ["Meet & start", "Choose your favourite, meet them, and start with confidence — documents included."]].map(([t, d], i) => (
              <div key={t} className="flex items-start gap-4 rounded-2xl border border-black/[0.07] bg-white p-5">
                <div className="w-10 h-10 shrink-0 rounded-full bg-[#7c2233] text-white font-display text-lg flex items-center justify-center">{i + 1}</div>
                <div><h3 className="font-display text-lg text-[#1f1a1b]">{t}</h3><p className="text-[15px] text-[#5a5254] mt-1">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-5 py-16">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1f1a1b]">FAQ about care in {city.name}</h2>
          <Accordion type="single" collapsible className="mt-8 w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`i${i}`} className="border border-black/[0.08] rounded-2xl mb-3 px-5">
                <AccordionTrigger className="text-left font-display text-lg text-[#1f1a1b] hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[15px] text-[#5a5254] leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Popular services */}
      <section className="bg-[#fdfaf7]">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1f1a1b]">Popular services in {city.name}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/${s.slug}-${city.slug}`} className="group flex items-center justify-between rounded-2xl border border-black/[0.08] bg-white px-6 py-5 hover:border-[#7c2233]/30 hover:shadow-[0_18px_40px_-24px_rgba(124,34,51,0.35)] transition-all">
                <span className="font-display text-lg text-[#1f1a1b]">{s.label} {city.name}</span>
                <ArrowRight className="w-5 h-5 text-[#7c2233] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Dark CTA */}
      <section className="bg-[#fdfaf7]">
        <div className="max-w-7xl mx-auto px-5 pb-20">
          <div className="rounded-3xl bg-[#17161d] px-8 py-14 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-white">Your caregiver in {city.name} is waiting</h2>
            <p className="text-white/70 mt-4 max-w-2xl mx-auto">Post now for free and receive applications from vetted caregivers in {city.name}. 1 month free.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={post} className="rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-7 py-3.5 transition-colors">Post a job free</button>
              <Link to={`/jobs?city=${city.slug}`} className="rounded-xl border border-white/25 text-white font-medium px-7 py-3.5 hover:bg-white/10 transition-colors">View open jobs in {city.name}</Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
